import { NexorClient } from "./client";
import { initChat as initChatImpl, type ChatInitOptions, type ChatHandle } from "./chat";
import type { ClientOptions } from "./types";

export { NexorClient } from "./client";
export {
  NexorError,
  NexorAPIError,
  NexorAuthError,
  NexorValidationError,
  NexorNetworkError,
} from "./errors";
export * from "./types";
export type {
  ChatInitOptions,
  ChatHandle,
  ChatLeadCapture,
  ChatChannels,
  ChatChannelKey,
} from "./chat";

/**
 * Singleton API. After `init({apiKey})`, call top-level helpers like
 * `createLead`, `getLead`, `initChat`, etc.
 *
 *   import nexor from "@getnexorai/sdk";
 *   nexor.init({ apiKey: "nx_live_…" });
 *   await nexor.createLead({ first_name: "Ada", email: "ada@example.com" });
 *
 * For multi-tenant / server-side use where a singleton doesn't fit, instantiate
 * the underlying client directly:
 *
 *   import { NexorClient } from "@getnexorai/sdk";
 *   const client = new NexorClient({ apiKey });
 */
export function createClient(opts: ClientOptions): NexorClient {
  return new NexorClient(opts);
}

let _client: NexorClient | null = null;

function getClient(): NexorClient {
  if (!_client) {
    throw new Error(
      "Nexor SDK: call `nexor.init({ apiKey })` before using the API.",
    );
  }
  return _client;
}

function init(opts: ClientOptions): NexorClient {
  _client = new NexorClient(opts);
  return _client;
}

function isInitialized(): boolean {
  return _client !== null;
}

function reset(): void {
  _client = null;
}

/**
 * The default export — a ready-to-use, lazily-initialized singleton.
 *
 * Every method below proxies to the underlying NexorClient created by `init()`,
 * so the surface mirrors NexorClient one-for-one with no boilerplate.
 */
export const nexor = {
  init,
  isInitialized,
  reset,
  get client(): NexorClient {
    return getClient();
  },

  // Leads
  createLead: ((...args) => getClient().createLead(...args)) as NexorClient["createLead"],
  createLeadsBulk: ((...args) =>
    getClient().createLeadsBulk(...args)) as NexorClient["createLeadsBulk"],
  updateLead: ((...args) => getClient().updateLead(...args)) as NexorClient["updateLead"],
  getLead: ((...args) => getClient().getLead(...args)) as NexorClient["getLead"],
  getLeadHistory: ((...args) =>
    getClient().getLeadHistory(...args)) as NexorClient["getLeadHistory"],
  isLeadPaused: ((...args) => getClient().isLeadPaused(...args)) as NexorClient["isLeadPaused"],
  stopAutomation: ((...args) => getClient().stopAutomation(...args)) as NexorClient["stopAutomation"],
  resumeAutomation: ((...args) =>
    getClient().resumeAutomation(...args)) as NexorClient["resumeAutomation"],
  syncLeadTags: ((...args) => getClient().syncLeadTags(...args)) as NexorClient["syncLeadTags"],
  listLeadMeetings: ((...args) =>
    getClient().listLeadMeetings(...args)) as NexorClient["listLeadMeetings"],

  // Workflows / Campaigns / Templates
  listWorkflows: ((...args) => getClient().listWorkflows(...args)) as NexorClient["listWorkflows"],
  listCampaigns: ((...args) => getClient().listCampaigns(...args)) as NexorClient["listCampaigns"],
  listTemplates: ((...args) => getClient().listTemplates(...args)) as NexorClient["listTemplates"],

  // Messages
  sendMessage: ((...args) => getClient().sendMessage(...args)) as NexorClient["sendMessage"],

  // Meetings
  createMeeting: ((...args) => getClient().createMeeting(...args)) as NexorClient["createMeeting"],
  createMeetingNotes: ((...args) =>
    getClient().createMeetingNotes(...args)) as NexorClient["createMeetingNotes"],

  // Chat widget
  initChat: (options: ChatInitOptions): ChatHandle =>
    initChatImpl(getClient(), options),
};

export default nexor;
